import { useCallback, useContext, useEffect, useReducer, useRef } from "react";
import { ApiContext } from "../../context/MyContext";
import { get as commsGet, post as commsPost } from "../../service/comms/commsApi";
import {
  FEED_LIMIT,
  getNotificationFeed,
  markAllNotificationsRead,
  markNotificationRead,
} from "../../service/comms/notificationsService";
import { useUserChannel } from "../../hooks/comms/useUserChannel";
import { normalizeNotification } from "./notificationText";

export const FEED_STATUS = Object.freeze({
  LOADING: "loading",
  READY: "ready",
  ERROR: "error",
});

const INITIAL = { items: [], unreadCount: 0, status: FEED_STATUS.LOADING, refreshing: false, notice: null };

const READ_FAILED = "Couldn't mark that as read. Try again.";
const READ_ALL_FAILED = "Couldn't mark everything read. Try again.";

const isUnread = (item) => !item.readAt;
const countUnread = (items) => items.filter(isUnread).length;

function reducer(state, action) {
  switch (action.type) {
    case "load_start":
      // Keep what we have on screen while a refresh runs.
      return state.status === FEED_STATUS.READY
        ? { ...state, refreshing: true }
        : { ...state, status: FEED_STATUS.LOADING, refreshing: true };
    case "load_ok":
      return {
        items: action.items,
        unreadCount: action.unreadCount,
        status: FEED_STATUS.READY,
        refreshing: false,
        notice: null,
      };
    case "load_fail":
      return { ...state, status: FEED_STATUS.ERROR, refreshing: false };
    case "live": {
      if (state.items.some((item) => item.id === action.item.id)) return state;
      return {
        ...state,
        items: [action.item, ...state.items].slice(0, FEED_LIMIT),
        unreadCount: state.unreadCount + (isUnread(action.item) ? 1 : 0),
      };
    }
    case "set_read": {
      const target = state.items.find((item) => item.id === action.id);
      if (!target || Boolean(target.readAt) === Boolean(action.readAt)) return state;
      return {
        ...state,
        items: state.items.map((item) => (item.id === action.id ? { ...item, readAt: action.readAt } : item)),
        unreadCount: Math.max(0, state.unreadCount + (action.readAt ? -1 : 1)),
      };
    }
    case "read_all":
      return {
        ...state,
        items: state.items.map((item) => (item.readAt ? item : { ...item, readAt: action.readAt })),
        unreadCount: 0,
      };
    case "restore":
      return { ...state, items: action.items, unreadCount: action.unreadCount };
    case "notice":
      return { ...state, notice: action.text };
    default:
      return state;
  }
}

/**
 * The caller's notification feed for the bell: newest 50 with the unread
 * count, live-prepended from the personal broadcast channel (the shared
 * subscription in useUserChannel, no second socket), with optimistic
 * read / read-all that roll back if the server refuses.
 *
 * @returns {{ items: Array<Object>, unreadCount: number, status: string,
 *   refreshing: boolean, notice: string|null,
 *   refresh: () => Promise<void>, markRead: (id: string) => Promise<void>,
 *   markAllRead: () => Promise<void> }}
 */
export function useNotificationFeed() {
  const { empId } = useContext(ApiContext);
  const [state, dispatch] = useReducer(reducer, INITIAL);
  const stateRef = useRef(state);
  stateRef.current = state;
  const requestSeqRef = useRef(0);

  const refresh = useCallback(async () => {
    const seq = requestSeqRef.current + 1;
    requestSeqRef.current = seq;
    dispatch({ type: "load_start" });
    try {
      const data = await getNotificationFeed(commsGet, { limit: FEED_LIMIT });
      if (seq !== requestSeqRef.current) return;
      const items = (Array.isArray(data?.events) ? data.events : [])
        .map((raw) => normalizeNotification(raw))
        .filter(Boolean);
      const unread = Number(data?.unread_count);
      dispatch({
        type: "load_ok",
        items,
        unreadCount: Number.isFinite(unread) && unread >= 0 ? unread : countUnread(items),
      });
    } catch (err) {
      if (seq === requestSeqRef.current) dispatch({ type: "load_fail" });
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const onNotification = useCallback((data) => {
    const item = normalizeNotification(data, new Date().toISOString());
    if (item) dispatch({ type: "live", item: { ...item, readAt: null } });
  }, []);

  useUserChannel(empId, { onNotification });

  const markRead = useCallback(async (id) => {
    const target = stateRef.current.items.find((item) => item.id === id);
    if (!target || target.readAt) return;
    dispatch({ type: "notice", text: null });
    dispatch({ type: "set_read", id, readAt: new Date().toISOString() });
    try {
      await markNotificationRead(commsPost, id);
    } catch (err) {
      dispatch({ type: "set_read", id, readAt: null });
      dispatch({ type: "notice", text: READ_FAILED });
    }
  }, []);

  const markAllRead = useCallback(async () => {
    const snapshot = stateRef.current;
    if (snapshot.unreadCount === 0) return;
    dispatch({ type: "notice", text: null });
    dispatch({ type: "read_all", readAt: new Date().toISOString() });
    try {
      await markAllNotificationsRead(commsPost);
    } catch (err) {
      dispatch({ type: "restore", items: snapshot.items, unreadCount: snapshot.unreadCount });
      dispatch({ type: "notice", text: READ_ALL_FAILED });
    }
  }, []);

  return { ...state, refresh, markRead, markAllRead };
}
